import {
  AutomatosClient,
  type AutomatosConfig,
  type BlogLayout,
  type BlogPostListResponse,
} from '@automatos/core';
import { createBlogShadowHost } from './dom/shadow-host';
import { PostListing } from './components/post-listing';
import { PostDetail } from './components/post-detail';
import { baseCSS } from './styles/base';
import { componentCSS } from './styles/components';
import { animationCSS } from './styles/animations';
import { aiLabelCSS } from './styles/ai-label';

export class BlogWidget {
  private client: AutomatosClient;
  private config: AutomatosConfig;
  private host!: HTMLElement;
  private container!: HTMLElement;
  private listing!: PostListing;
  private detail!: PostDetail;

  private cache = new Map<number, BlogPostListResponse>();
  /** The slug of the post being shown or loaded; null on the listing. */
  private openSlug: string | null = null;

  constructor(config: AutomatosConfig) {
    this.config = config;
    this.client = new AutomatosClient(config);
    this.mount();
    this.loadPage(1);

    // Pre-authenticate for server keys
    if (!config.apiKey.startsWith('ak_pub_')) {
      this.client.authenticate().catch(() => {
        // Will retry on first API call
      });
    }
  }

  destroy(): void {
    this.openSlug = null;
    this.detail.leave();
    this.client.destroy();
    this.host.remove();
  }

  private mount(): void {
    const css = [baseCSS, componentCSS, animationCSS, aiLabelCSS].join('\n');
    const { host, container } = createBlogShadowHost(this.config, css);
    this.host = host;
    this.container = container;

    const layout: BlogLayout = this.config.blogConfig?.layout ?? 'grid';
    const postsPerPage = this.config.blogConfig?.postsPerPage ?? 6;

    this.listing = new PostListing(layout, postsPerPage);
    this.listing.onPostClick = (slug) => this.navigateToPost(slug);
    this.listing.onPageChange = (page) => this.loadPage(page);

    this.detail = new PostDetail();
    this.detail.onBack = () => this.navigateToListing();

    this.container.appendChild(this.listing.el);
  }

  private async loadPage(page: number): Promise<void> {

    const cached = this.cache.get(page);
    if (cached) {
      this.listing.showPosts(cached.posts, cached.page, cached.total_pages);
      return;
    }

    this.listing.showLoading();

    try {
      const response = await this.client.listPosts({
        page,
        perPage: this.config.blogConfig?.postsPerPage ?? 6,
        category: this.config.blogConfig?.category,
        tag: this.config.blogConfig?.tag,
      });

      this.cache.set(page, response);
      this.listing.showPosts(response.posts, response.page, response.total_pages);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load posts';
      this.listing.showError(message);
    }
  }

  private async navigateToPost(slug: string): Promise<void> {
    this.openSlug = slug;
    this.container.replaceChildren(this.detail.el);
    this.detail.showLoading();

    try {
      const post = await this.client.getPost(slug);
      // The reader went back (or opened another post) while this one loaded:
      // don't render it, or its JSON-LD would land on the listing page.
      if (this.openSlug !== slug) return;
      this.detail.showPost(post);
    } catch (err) {
      if (this.openSlug !== slug) return;
      const message = err instanceof Error ? err.message : 'Failed to load post';
      // Show error inline in detail view, with back button still available
      this.detail.showError(message);
    }
  }

  private navigateToListing(): void {
    this.openSlug = null;
    this.detail.leave();
    this.container.replaceChildren(this.listing.el);
  }
}
